import Icon from 'components/atoms/Icon';
import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import ProjectJobRoleSelectFormField from 'components/molecules/SelectFormField/ProjectJobRoleSelectFormField/ProjectJobRoleSelectFormField';
import ReportNameSelectFormField from 'components/molecules/SelectFormField/ReportNameSelectFormField/ReportNameSelectFormField';
import {
  ProjectJobRoleFindManyQuery,
  ReportRenderType,
  useStageResultsReportRenderCreateMutation,
} from 'generated/graphql';
import { usePollingReportRenderQuery } from 'helpers/reportRender';
import { formatTitleCase } from 'helpers/strings';
import { TSelectFormFieldValue } from 'interfaces/forms/form';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './ReportsManagement.module.scss';

const NewReport = ({
  stageCandidateId,
  isDisabled,
  stageId,
  projectId,
  companyId,
  projectJobRoles,
  isProjectMultimatch,
  selectedJobRoleId,
  setSelectedJobRoleId,
}: {
  stageCandidateId: number;
  isDisabled: boolean;
  stageId: number;
  projectId: number;
  companyId: number;
  projectJobRoles?: ProjectJobRoleFindManyQuery;
  isProjectMultimatch: boolean;
  selectedJobRoleId: TSelectFormFieldValue;
  setSelectedJobRoleId: (value: TSelectFormFieldValue) => void;
}) => {
  const { handleMsgType } = useNotification();
  const [showGeneratedReport, setShowGeneratedReport] = useState(false);
  const [reportName, setReportName] = useState<TSelectFormFieldValue>('');

  const reportRenderSubIdRef = useRef<string | null>('new');

  const handleReportNameChange = (value: TSelectFormFieldValue) => {
    setReportName(value);
  };

  const { startPolling, isPolling, reportRenderQueryResponse } =
    usePollingReportRenderQuery(reportRenderSubIdRef.current, true);

  useEffect(() => {
    if (reportRenderQueryResponse.data?.ReportRender) {
      setShowGeneratedReport(true);
    }
  }, [reportRenderQueryResponse.data?.ReportRender]);

  const handleSelectedJobRoleId = (value: TSelectFormFieldValue) => {
    setSelectedJobRoleId(value);
  };

  const [
    reportRenderCreateMutation,
    { loading: isReportRenderCreateLoading, data: reportRenderCreateData },
  ] = useStageResultsReportRenderCreateMutation({
    onError: (error) => {
      handleMsgType({
        type: TNotification.error,
        title: 'Something went wrong',
        message: error?.message,
        dismissable: true,
      });
    },
    onCompleted: (data) => {
      const report = data?.StageResultsReportRenderCreate;
      if (report) {
        reportRenderSubIdRef.current = report.subId;
        startPolling();
      }
    },
  });

  const handleGenerateReport = () => {
    reportRenderCreateMutation({
      variables: {
        reportId: Number(reportName),
        dataSourceId: Number(stageCandidateId),
        reportRenderType: ReportRenderType.Pdf,
        projectJobRoleId: Number(selectedJobRoleId),
      },
    });
  };

  const handleOpenGeneratedReport = () => {
    if (reportRenderQueryResponse.data?.ReportRender?.blob?.url) {
      window.open(
        `${reportRenderQueryResponse.data?.ReportRender?.blob?.url}`,
        '_blank',
        'noopener,noreferrer',
      );
    }
  };
  const isGenerateReportDisabled =
    isDisabled || !reportName || !selectedJobRoleId;

  return (
    <>
      <p className={styles.sectionTitle}>
        Create new report from existing soft skills scores
      </p>
      <div className={styles.reportNameContainer}>
        <ReportNameSelectFormField
          id="reportName"
          name="reportName"
          label="Report name"
          value={reportName}
          onChange={handleReportNameChange}
          isDisabled={isDisabled}
          stageId={stageId}
          projectId={Number(projectId)}
          companyId={Number(companyId)}
        />
        {isProjectMultimatch && (
          <ProjectJobRoleSelectFormField
            id="projectJobRoleSelect"
            name="projectJobRole"
            label="Role"
            onChange={handleSelectedJobRoleId}
            projectJobRoles={projectJobRoles?.ProjectJobRoleFindMany ?? []}
          />
        )}
        <Button
          variant="primary"
          disabled={isGenerateReportDisabled}
          loading={isReportRenderCreateLoading || isPolling}
          onClick={handleGenerateReport}
        >
          Generate
        </Button>
      </div>
      {reportRenderCreateData?.StageResultsReportRenderCreate && (
        <div className={styles.generatedReportInfo}>
          <InfoItem
            label="Report subId"
            value={
              reportRenderCreateData?.StageResultsReportRenderCreate?.subId
            }
            hasCopyButton
          />
          <InfoItem
            label="Status"
            value={formatTitleCase(
              reportRenderQueryResponse?.data?.ReportRender
                ?.reportRenderStatus || '',
            )}
          />
          {showGeneratedReport && !isPolling && (
            <Button
              variant="secondary"
              size="small"
              leftIcon={<Icon icon="openTab" />}
              onClick={handleOpenGeneratedReport}
              className={styles.openGeneratedReportButton}
              disabled={
                Boolean(
                  !reportRenderQueryResponse?.data?.ReportRender?.blob?.url,
                ) || isDisabled
              }
            >
              Open report in new tab
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default NewReport;
